/* eslint-disable react/no-multi-comp */
import React, {useCallback, useState, useMemo, useRef} from 'react';
import {shallowEqual, useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {useDocumentId, useEngagementId, useCanvasFormMetadata} from '../../../../../../util/customHooks';
import {
	formBodyType,
	entities,
	itControlApproachType,
	extendedPagingOptions,
	specialBodyPagingOptions
} from '../../../../../../util/uiconstants';
import {labels, GetCardTypeForBody} from '../../../../../../util/utils';
import BodySpecialFetch from './BodySpecialFetch';
import BodySpecialToolbar from './BodySpecialToolbar';
import BodySpecialToolbarButton from './BodySpecialToolbarButton';
import ViewEntityContainer from './ViewEntities/ViewEntityContainer';
import RelateEntityContainer from './RelateEntities/RelateEntitiesContainer';
import {useRelateEntitiesModal} from './RelateEntities/relateEntities.utils';
import ISA315ScotItApplicationBodyList from './ISA315ScotItAppBody/ISA315ScotItApplicationBodyList';
import ISA315ItAppScotBodyItAppList from './ISA315ItAppScotBody/ISA315ItAppScotBodyItAppList';
import ISA315ItProcessTaskList from './ISA315ItProcessTaskList';
import ISA315ItProcessItRiskToolbar from './ISA315ItProcessItRiskBody/ISA315ItProcessItRiskToolbar';
import ISA315ItProcessItRiskList from './ISA315ItProcessItRiskBody/ISA315ItProcessItRiskList';
import ISA315ItGCTestStrategyList from './ISA315ItGCTestStrategy/ISA315ItGCTestStrategyList';
import ISA315SOtoSCOTList from './ISA315SOScotBody/ISA315SOScotList';
import ISA315ManageITSPList from './ISA315ManageITSP/ISA315ManageITSPList';
import ISA315ManageITSPToolbar from './ISA315ManageITSP/ISA315ManageITSPToolbar';
import ISA315ItGcTestStrategyToolbar from './ISA315ItGCTestStrategy/ISA315ItGcTestStrategyToolbar';
import {openItApplicationModal} from './CreateUpdateEntities/createUpdateEntities.utils';
import {CreateUpdateEntityContainer} from './CreateUpdateEntities/index';
import AresSpecialBodyContainer from '../AresSpecialBodyContainer';
import ISA315ItApplicationToolbar from './ISA315ScotItAppBody/ISA315ItApplicationToolbar';
import CreateEditITProcessModalWrapper from '../../../../../Common/EntityCrud/CreateEditITProcessModalWrapper';
import {getITProcessById} from '../../../../../../actions/ITProcess/itprocessactions';

const config = {
	[formBodyType.ISA315ScotItApplication]: <ISA315ScotItApplicationBodyList />,
	[formBodyType.ISA315ItApplicationScot]: <ISA315ItAppScotBodyItAppList />,
	[formBodyType.ISA315ItProcessTask]: <ISA315ItProcessTaskList />,
	[formBodyType.ISA315ItProcessItRisk]: <ISA315ItProcessItRiskList />,
	[formBodyType.ISA315ItGCTestStrategy]: <ISA315ItGCTestStrategyList />,
	[formBodyType.ISA315SoScot]: <ISA315SOtoSCOTList />,
	[formBodyType.ISA315ManageItSp]: <ISA315ManageITSPList />
};

const toolbarButton = {
	[formBodyType.ISA315ScotItApplication]: {
		label: labels.addITApp,
		entityId: entities.ItSoApplication,
		render: <ISA315ItApplicationToolbar />
	},
	[formBodyType.ISA315ItApplicationScot]: {
		label: labels.addITApp,
		entityId: entities.ItSoApplication,
		isStandalone: true,
		render: <ISA315ItApplicationToolbar />
	},
	[formBodyType.ISA315ItProcessItRisk]: {
		label: labels.addItRiskBtnTitle,
		entityId: entities.ItRisk,
		render: <ISA315ItProcessItRiskToolbar />
	},
	[formBodyType.ISA315SoScot]: {
		label: labels.addItSOBtnTitle,
		entityId: entities.ItSoApplication,
		isServiceOrganization: true
	},
	[formBodyType.ISA315ItGCTestStrategy]: {
		render: <ISA315ItGcTestStrategyToolbar />
	},
	[formBodyType.ISA315ManageItSp]: {
		label: labels.addITSP,
		entityId: entities.ItControl,
		itApproachTypeId: itControlApproachType.substantive,
		render: <ISA315ManageITSPToolbar />
	}
};

const toolbar = {
	[formBodyType.ISA315ScotItApplication]: {
		searchPlaceholder: labels.searchScot
	},
	[formBodyType.ISA315ItApplicationScot]: {
		searchPlaceholder: labels.searchItApplication
	},
	[formBodyType.ISA315ItProcessTask]: {
		searchPlaceholder: labels.itProcessSearchPlaceholder
	},
	[formBodyType.ISA315ItProcessItRisk]: {
		searchPlaceholder: labels.itProcessSearchPlaceholder
	},
	[formBodyType.ISA315ItGCTestStrategy]: {
		searchPlaceholder: labels.searchItGC
	},
	[formBodyType.ISA315SoScot]: {
		searchPlaceholder: labels.searchItSO
	},
	[formBodyType.ISA315ManageItSp]: {
		searchPlaceholder: labels.searchPlaceholderManageITSP
	}
};

const pagingOptions = {
	[formBodyType.ISA315ScotItApplication]: specialBodyPagingOptions.options,
	[formBodyType.ISA315ItApplicationScot]: specialBodyPagingOptions.options,
	[formBodyType.ISA315ItProcessTask]: specialBodyPagingOptions.options,
	[formBodyType.ISA315ItProcessItRisk]: specialBodyPagingOptions.options,
	[formBodyType.ISA315ItGCTestStrategy]: specialBodyPagingOptions.options,
	[formBodyType.ISA315SoScot]: specialBodyPagingOptions.options,
	[formBodyType.ISA315ManageItSp]: specialBodyPagingOptions.options
};

export default function BodySpecialContainer({
	body,
	className,
	bodyId,
	bodyTypeId,
	cardTypeId,
	evidenceDocumentReferenceCount,
	priorPeriodDocumentReferenceCount,
	getBodiesBySectionId,
	guidanceFilter,
	headerId,
	isAresView,
	isSnapshotView,
	sectionId,
	showGuidance,
	updateBodyOptions
}) {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const documentId = useDocumentId();

	const modalRef = useRef(),
		viewModalRef = useRef();

	const [relateContainerProps, relateModalRef, openRelateModal] = useRelateEntitiesModal();

	// Shared form - target engagement
	const {isTargetEngagement, documentEngagementId} = useCanvasFormMetadata();

	const [viewContainerProps, setViewContainerProps] = useState({}),
		[containerProps, setContainerProps] = useState({}),
		[filters, setFilters] = useState({
			page: 1,
			pageSize: pagingOptions[bodyTypeId] ? pagingOptions[bodyTypeId][0] : extendedPagingOptions.options[0],
			pagingOptions: pagingOptions[bodyTypeId] ? pagingOptions[bodyTypeId] : extendedPagingOptions.options
		}),
		[forceUpdate, setForceUpdate] = useState(0),
		[showAddItProcessModal, setShowAddItProcessModal] = useState(false);

	const allIds = useSelector((state) => state.bodiesSpecial.metadata[bodyId]?.allIds, shallowEqual);

	const overrideFilters = useCallback((obj) => {
		setFilters({...filters, ...obj});
	}, []);

	// Open IT APP/SO modal.
	const isITApp =
		bodyTypeId === formBodyType.ISA315ScotItApplication || bodyTypeId === formBodyType.ISA315ItApplicationScot;

	const showToolbarButton =
		!isTargetEngagement &&
		!(bodyTypeId === formBodyType.ISA315ItGCTestStrategy || bodyTypeId === formBodyType.ISA315ItProcessTask);

	const forceUpdateCallback = () => {
		setForceUpdate(forceUpdate + 1);
	};

	const openViewModal = useCallback(
		(clickArgs) => {
			if (viewModalRef.current.toggleModal) {
				setViewContainerProps(clickArgs);
				viewModalRef.current.toggleModal();
			}
		},
		[viewModalRef]
	);

	const onCreateUpdate = useCallback(
		(clickArgs) => {
			if (modalRef.current.toggleModal) {
				setContainerProps(clickArgs);
				modalRef.current.toggleModal();
			}
		},
		[modalRef]
	);

	// Open IT Process modal.
	const addItProcess = () => {
		setShowAddItProcessModal(true);
	};

	const addItApp = () => {
		openItApplicationModal(
			onCreateUpdate({
				entityId: entities.ItSoApplication,
				isServiceOrganization: isITApp ? false : true
			})
		);
	};

	const bodyModel = useMemo(
		() => ({
			id: bodyId,
			bodyTypeId,
			cardTypeId,
			engagementComments: body.engagementComments,
			evidenceDocumentReferenceCount: body.evidenceDocumentReferenceCount,
			priorPeriodDocumentReferenceCount: body.priorPeriodDocumentReferenceCount,
			guidances: body.guidances
		}),
		[bodyId, bodyTypeId, cardTypeId, body, evidenceDocumentReferenceCount, priorPeriodDocumentReferenceCount]
	);

	const noDataPlaceholder = useMemo(
		() => ({
			[formBodyType.ISA315ScotItApplication]: {
				text: labels.scotNoDataPlaceholder
			},
			[formBodyType.ISA315ItApplicationScot]: {
				text: labels.itAppScotNoDataPlaceholder,
				onClickConfig: {itAppScotNoDataPlaceholderAddItApp: addItApp}
			},
			[formBodyType.ISA315ItProcessTask]: {
				text: labels.itProcessNoDataPlaceholder,
				onClickConfig: {
					addItProcess: addItProcess
				}
			},
			[formBodyType.ISA315ItProcessItRisk]: {
				text: labels.itProcessItRiskNoDataPlaceholder,
				onClickConfig: {identifyItProcess: addItProcess}
			},
			[formBodyType.ISA315ItGCTestStrategy]: {text: labels.itGCNoDataPlaceHolder},
			[formBodyType.ISA315SoScot]: {
				text: isTargetEngagement ? labels.noItSoDataPlaceHolder : labels.itSoNoDataPlaceHolder,
				onClickConfig: {identifyAnSo: addItApp}
			},
			[formBodyType.ISA315ManageItSp]: {text: labels.noManageITSPDataPlaceholder}
		}),
		[isTargetEngagement]
	);

	const handleOnFilterChange = useCallback((name, value) => {
		setFilters((state) => ({...state, [name]: value, page: name === 'page' ? value : 1}));
	}, []);

	// Toolbar contents.
	// Note: Using cloneElement to pass props that are accessible in the scope of the BodySpecialContainer function, and to keep the global config as simple as possible
	const toolbarButtonConfig = toolbarButton[bodyTypeId];
	const toolbarContent =
		toolbarButtonConfig && toolbarButtonConfig.render ? (
			React.cloneElement(toolbarButtonConfig.render, {
				...toolbarButtonConfig,
				bodyId,
				isTargetEngagement,
				openViewModal: openViewModal
			})
		) : (
			// Default toolbar button
			<>
				{showToolbarButton && (
					<BodySpecialToolbarButton {...toolbarButtonConfig} bodyId={bodyId} bodyTypeId={bodyTypeId} />
				)}
			</>
		);

	const renderContent = React.cloneElement(config[bodyTypeId], {openRelateModal, filters, overrideFilters});

	const handleOnItProcessCreate = (response) => {
		dispatch(getITProcessById(engagementId, response.id, {IncludeRelatedTask: true}));
	};

	const showSearchBar = filters.searchString !== undefined || allIds?.length > 0;

	return (
		<StyledBodySpecialContainer
			className={`StyledBodySpecialContainer bodyWrapper ${className} ${
				isAresView ? 'AresView' : 'KronosView'
			} ${GetCardTypeForBody(body, isAresView)}`}
		>
			<section className={`bodyWrapperSection ${GetCardTypeForBody(body, isAresView)}`}>
				{body && (
					<AresSpecialBodyContainer
						showGuidance={showGuidance}
						guidanceFilter={guidanceFilter}
						bodyContent={body}
						headerId={headerId}
						sectionId={sectionId}
						bodyId={bodyId}
						updateBodyOptions={updateBodyOptions}
						getBodiesBySectionId={getBodiesBySectionId}
						isSnapshotView={isSnapshotView}
						showDescription={!isAresView}
					/>
				)}
				{/*Guidances being here is not a bad idea but since we have it encapsulated in BodyKnowledgeDescription, we would be showing them twice.*/}
				{/* {body.guidances && <Guidance options={body} isSnapshotView={isSnapshotView} guidanceDetails={body.guidances} />} */}
				<ViewEntityContainer
					{...viewContainerProps}
					engagementId={engagementId}
					documentId={documentId}
					documentEngagementId={documentEngagementId}
					bodyId={bodyId}
					ref={viewModalRef}
					isAresView={isAresView}
				/>
				<CreateUpdateEntityContainer
					{...containerProps}
					engagementId={Number(engagementId)}
					documentId={documentId}
					documentEngagementId={documentEngagementId}
					bodyId={bodyId}
					ref={modalRef}
				/>
				<RelateEntityContainer {...relateContainerProps} bodyId={bodyId} ref={relateModalRef} />
				{showAddItProcessModal && (
					<CreateEditITProcessModalWrapper
						isCreate
						showModal={showAddItProcessModal}
						setShowModal={setShowAddItProcessModal}
						onSaveComplete={handleOnItProcessCreate}
					/>
				)}
				{showSearchBar && (
					<BodySpecialToolbar bodyTypeId={bodyTypeId} {...toolbar[bodyTypeId]} onFilterChange={handleOnFilterChange}>
						{toolbarContent}
					</BodySpecialToolbar>
				)}
				<BodySpecialFetch
					bodyId={bodyId}
					bodyTypeId={bodyTypeId}
					filters={filters}
					noDataPlaceholder={noDataPlaceholder[bodyTypeId]}
					onFilterChange={handleOnFilterChange}
					isTargetEngagement={isTargetEngagement}
					forceUpdate={forceUpdate}
					forceUpdateCallback={forceUpdateCallback}
				>
					{renderContent}
				</BodySpecialFetch>
			</section>
		</StyledBodySpecialContainer>
	);
}

BodySpecialContainer.propTypes = {
	className: PropTypes.string,
	bodyDescriptionText: PropTypes.string.isRequired,
	bodyId: PropTypes.number.isRequired,
	bodyTypeId: PropTypes.number.isRequired,
	cardTypeId: PropTypes.number.isRequired,
	evidenceDocumentReferenceCount: PropTypes.number,
	priorPeriodDocumentReferenceCount: PropTypes.number,
	getBodiesBySectionId: PropTypes.func.isRequired,
	guidanceFilter: PropTypes.func,
	headerId: PropTypes.number.isRequired,
	isAresView: PropTypes.bool,
	isDuplicate: PropTypes.bool,
	isSnapshotView: PropTypes.bool,
	sectionId: PropTypes.number.isRequired,
	showGuidance: PropTypes.bool,
	showRelateDocumentsIcon: PropTypes.bool,
	updateBodyOptions: PropTypes.func
};

BodySpecialContainer.defaultProps = {
	className: 'kronos-special-body-container',
	bodyId: 0,
	// TODO:
	//documentReferenceCount: PropTypes.number,
	isAresView: false,
	// isDuplicate: PropTypes.bool,
	isSnapshotView: false,
	showGuidance: true
	// showRelateDocumentsIcon: false
};

const StyledBodySpecialContainer = styled.section`
	height: 100%;
	padding: var(--px-10) var(--px-30);
	position: relative;
	&.AresView {
		padding: var(--px-10) 0;
		.bodyWrapperSection {
			padding: var(--px-10) 0;
		}

		.StyledBodySpecialNonAccordionTrigger {
			padding-left: var(--px-48);
			height: var(--px-85);
		}
		.StyledBodySpecialAccordionHeader {
			.StyledBodySpecialAccordionHeaderColumn {
				line-height: var(--px-28);
				padding: var(--px-16) var(--px-10) var(--px-17) 0;
			}
			.motif-accordion-icons {
				margin-top: 0;
				margin-right: var(--px-12);
			}
		}
		.StyledBodySpecialListItemNoDataPlaceholder {
			padding: var(--px-30) var(--px-20);
			padding-left: var(--px-46);
			background-color: var(--neutrals-50);
		}
		.BodySpecialListItemNoDataPlaceholder {
			border: var(--px-1) solid var(--neutrals-200);
			background-color: var(--neutrals-50);
			border-top: none;
		}
		.noDataPlaceholder {
			background-color: var(--neutrals-50);
		}
		.StyledBodySpecialToolbar {
			margin: var(--px-20) 0 !important;
		}
	}
	.bodyWrapperSection {
		height: 100%;
		display: flex;
		flex-direction: column;
		padding: var(--px-10) 0 var(--px-10) var(--px-10);
	}
`;
